import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCALE = ['Very Poor', 'Poor', 'Average', 'Good', 'Excellent'];

// Section A — Q1..Q8 (Q7 is meeting-room only)
const RATING_QUESTIONS = [
  { key: 'rating_overall', label: '1. Overall experience' },
  { key: 'rating_booking', label: '2. Booking & check-in', hint: 'Ease of booking, welcome on arrival, clear instructions' },
  { key: 'rating_staff', label: '3. Reception & staff support', hint: 'Courtesy, helpfulness, speed of response' },
  { key: 'rating_cleanliness', label: '4. Cleanliness & upkeep', hint: 'Workspace or room, washrooms, pantry' },
  { key: 'rating_comfort', label: '5. Space & comfort', hint: 'Seating, lighting, temperature, noise, privacy' },
  { key: 'rating_wifi', label: '6. Wi-Fi & connectivity', hint: 'Speed and reliability' },
  { key: 'rating_av', label: '7. Meeting room setup & AV', hint: 'Ready on time, display, video calls, refreshments', meetingOnly: true },
  { key: 'rating_value', label: '8. Value for money' },
];

const READINESS = [
  { value: 'yes_fully', label: 'Yes, fully' },
  { value: 'mostly', label: 'Mostly, with minor issues' },
  { value: 'no', label: 'No, something important was not right' },
];

const RECOMMEND = ['Very unlikely', 'Unlikely', 'Undecided', 'Likely', 'Very likely'];

const SAFETY_CATEGORIES = [
  { value: 'cleanliness', label: 'Cleanliness (washrooms, pantry, workspace, room)' },
  { value: 'maintenance', label: 'Maintenance or damage' },
  { value: 'fire_safety', label: 'Fire safety, exits or signage' },
  { value: 'electrical_hazard', label: 'Electrical or physical hazard' },
  { value: 'security', label: 'Security, access or belongings' },
  { value: 'other', label: 'Other' },
];

const SERVICE_LABEL = { day_pass: 'Day pass', meeting_room: 'Meeting room' };

const SectionTitle = ({ letter, children }) => (
  <div className="flex items-center gap-3 pt-2">
    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-semibold">{letter}</span>
    <h3 className="font-heading text-lg tracking-wide uppercase">{children}</h3>
  </div>
);

const ScaleRating = ({ value, onChange, label, hint, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <div>
      <p className="font-medium text-sm">{label}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
    <div className="grid grid-cols-5 gap-1.5">
      {SCALE.map((name, i) => {
        const n = i + 1;
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-pressed={active}
            data-testid={`${testid}-${n}`}
            className={`rounded-md border py-2 text-center transition-colors ${
              active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted/60'
            }`}
          >
            <span className="block text-base font-semibold leading-tight">{n}</span>
            <span className="block text-[10px] leading-tight opacity-80">{name}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const ChoiceGroup = ({ options, value, onChange, testid, stack = false }) => (
  <div className={stack ? 'space-y-2' : 'flex flex-wrap gap-2'} data-testid={testid}>
    {options.map((o) => {
      const v = typeof o === 'string' ? o : o.value;
      const label = typeof o === 'string' ? o : o.label;
      const active = value === v;
      return (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-pressed={active}
          className={`rounded-md border px-3 py-2 text-sm text-left transition-colors ${stack ? 'w-full' : ''} ${
            active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted/60'
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const FeedbackPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [ratings, setRatings] = useState({});
  const [readiness, setReadiness] = useState('');
  const [readinessIssue, setReadinessIssue] = useState('');
  const [recommend, setRecommend] = useState(0); // 1..5
  const [safetyConcern, setSafetyConcern] = useState(null); // true | false | null
  const [safetyCategories, setSafetyCategories] = useState([]);
  const [safetyDetails, setSafetyDetails] = useState('');
  const [liked, setLiked] = useState('');
  const [improve, setImprove] = useState('');
  const [contactOk, setContactOk] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactInfo, setContactInfo] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/feedback/by-token/${token}`);
        if (!cancelled) setInfo(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Could not load feedback form');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const isMeetingRoom = info?.service_type === 'meeting_room';
  const questions = RATING_QUESTIONS.filter((q) => !q.meetingOnly || isMeetingRoom);

  const toggleCategory = (value) =>
    setSafetyCategories((prev) => (prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]));

  const handleSubmit = async () => {
    if (questions.some((q) => !ratings[q.key])) {
      toast.error('Please rate every aspect in section A');
      return;
    }
    if (!readiness) { toast.error('Please answer question 9'); return; }
    if (!recommend) { toast.error('Please answer question 10'); return; }
    if (safetyConcern === null) { toast.error('Please answer question 11'); return; }
    if (safetyConcern && safetyCategories.length === 0) {
      toast.error('Please tick what you noticed in question 11');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/submit/${token}`, {
        ...ratings,
        readiness,
        readiness_issue: readiness === 'yes_fully' ? '' : readinessIssue,
        recommend,
        safety_concern: safetyConcern,
        safety_categories: safetyConcern ? safetyCategories : [],
        safety_details: safetyConcern ? safetyDetails : '',
        liked,
        improve,
        contact_ok: contactOk === true,
        contact_name: contactOk ? contactName : '',
        contact_info: contactOk ? contactInfo : '',
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="feedback-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500" />
            <h2 className="text-xl font-heading">Cannot open feedback form</h2>
            <p className="text-muted-foreground">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>Back to Clayworks</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="feedback-success">
        <Card className="max-w-md w-full">
          <CardContent className="p-10 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
            <h2 className="text-2xl font-heading">Thank you for your time!</h2>
            <p className="text-muted-foreground">Your feedback has been recorded. We use every review to keep improving the Clayworks experience.</p>
            <Button onClick={() => navigate('/')}>Back to Clayworks</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = [
    ['Centre', [info?.venue_name, info?.venue_city].filter(Boolean).join(', ')],
    ['Date & time', [info?.booking_date, info?.start_time && `${info.start_time} - ${info.end_time}`].filter(Boolean).join(' · ')],
    ['Service used', [SERVICE_LABEL[info?.service_type], info?.space_name].filter(Boolean).join(' · ')],
    ['Booking ref', info?.booking_ref],
  ];

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="feedback-form-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading text-primary tracking-wide">CLAYWORKS</h1>
          <p className="text-xs text-muted-foreground tracking-widest mt-1">GUEST EXPERIENCE FEEDBACK</p>
        </div>

        <Card>
          <CardContent className="p-6 space-y-7">
            <div>
              <h2 className="text-2xl font-heading mb-1">How was your experience{info?.user_name ? `, ${info.user_name}` : ''}?</h2>
              <p className="text-sm text-muted-foreground">
                Thank you for choosing Clayworks. This takes about two minutes, and your honest feedback helps us improve our centres, our team and your experience.
              </p>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-muted/40 p-4 text-sm" data-testid="feedback-booking-summary">
              {summary.filter(([, v]) => v).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-muted-foreground">{k}</dt>
                  <dd className="font-medium">{v}</dd>
                </div>
              ))}
            </dl>

            {/* A — YOUR EXPERIENCE */}
            <section className="space-y-5">
              <SectionTitle letter="A">Your experience</SectionTitle>
              <p className="text-sm text-muted-foreground">Please rate each aspect of your experience.</p>
              {questions.map((q) => (
                <ScaleRating
                  key={q.key}
                  label={`${q.label} *`}
                  hint={q.hint}
                  value={ratings[q.key] || 0}
                  onChange={(n) => setRatings((r) => ({ ...r, [q.key]: n }))}
                  testid={q.key.replace('_', '-')}
                />
              ))}
            </section>

            {/* B — BOOKING AND NEXT STEPS */}
            <section className="space-y-5">
              <SectionTitle letter="B">Booking and next steps</SectionTitle>
              <div className="space-y-2">
                <p className="font-medium text-sm">9. Was everything ready and working as you booked it? *</p>
                <ChoiceGroup options={READINESS} value={readiness} onChange={setReadiness} stack testid="readiness" />
              </div>
              {(readiness === 'mostly' || readiness === 'no') && (
                <div className="space-y-2">
                  <p className="text-sm">What went wrong? <span className="text-muted-foreground">(e.g. room not ready, equipment fault, billing, access, Wi-Fi)</span></p>
                  <Textarea rows={3} value={readinessIssue} onChange={(e) => setReadinessIssue(e.target.value)} maxLength={2000} data-testid="readiness-issue" />
                </div>
              )}
              <div className="space-y-2">
                <p className="font-medium text-sm">10. How likely are you to use Clayworks again, or recommend us? *</p>
                <ChoiceGroup
                  options={RECOMMEND.map((label, i) => ({ value: i + 1, label }))}
                  value={recommend}
                  onChange={setRecommend}
                  testid="recommend"
                />
              </div>
            </section>

            {/* C — HYGIENE AND SAFETY */}
            <section className="space-y-5">
              <SectionTitle letter="C">Hygiene and safety</SectionTitle>
              <div className="space-y-2">
                <p className="font-medium text-sm">11. Did you notice any hygiene, maintenance, safety or security concern during your stay? *</p>
                <ChoiceGroup
                  options={[{ value: false, label: 'No' }, { value: true, label: 'Yes (please tell us more below)' }]}
                  value={safetyConcern}
                  onChange={setSafetyConcern}
                  testid="safety-concern"
                />
              </div>
              {safetyConcern === true && (
                <div className="space-y-4" data-testid="safety-details-block">
                  <div className="space-y-2">
                    <p className="text-sm">Tick all that apply:</p>
                    {SAFETY_CATEGORIES.map((c) => (
                      <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={safetyCategories.includes(c.value)}
                          onChange={() => toggleCategory(c.value)}
                          className="h-4 w-4 accent-[hsl(var(--primary))]"
                          data-testid={`safety-cat-${c.value}`}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm">What did you notice, and where in the centre?</p>
                    <Textarea rows={3} value={safetyDetails} onChange={(e) => setSafetyDetails(e.target.value)} maxLength={2000} data-testid="safety-details" />
                  </div>
                </div>
              )}
            </section>

            {/* D — IN YOUR WORDS */}
            <section className="space-y-5">
              <SectionTitle letter="D">In your words</SectionTitle>
              <div className="space-y-2">
                <p className="font-medium text-sm">12. What did we do well?</p>
                <Textarea rows={3} value={liked} onChange={(e) => setLiked(e.target.value)} maxLength={2000} data-testid="feedback-liked" />
              </div>
              <div className="space-y-2">
                <p className="font-medium text-sm">13. What could have been better?</p>
                <Textarea rows={3} value={improve} onChange={(e) => setImprove(e.target.value)} maxLength={2000} data-testid="feedback-improve" />
              </div>
              <div className="space-y-2">
                <p className="font-medium text-sm">May we contact you about your feedback?</p>
                <ChoiceGroup
                  options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]}
                  value={contactOk}
                  onChange={setContactOk}
                  testid="contact-ok"
                />
              </div>
              {contactOk === true && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input placeholder="Name (optional)" value={contactName} onChange={(e) => setContactName(e.target.value)} maxLength={120} data-testid="contact-name" />
                  <Input placeholder="Phone / email" value={contactInfo} onChange={(e) => setContactInfo(e.target.value)} maxLength={160} data-testid="contact-info" />
                </div>
              )}
            </section>

            <Button onClick={handleSubmit} disabled={submitting} className="w-full" size="lg" data-testid="feedback-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Feedback
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Clayworks Spaces Technologies Pvt Ltd
        </p>
      </div>
    </div>
  );
};

export default FeedbackPage;
